import PageHeader from "../components/PageHeader";
import {
    FaShieldAlt,
    FaLock,
    FaUserSecret,
    FaCookieBite,
    FaInfoCircle,
    FaEnvelope
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function PrivacyPolicy() {
    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>Privacy Policy | UAE Dubai Hotels</title>

                <meta
                    name="description"
                    content="Read the Privacy Policy of UAE Dubai Hotels to learn how we collect, use, store, and protect your personal information when you browse our website or book hotels across the UAE."
                />

                <meta
                    name="keywords"
                    content="Privacy Policy, UAE Dubai Hotels, hotel booking privacy, data protection, personal information, cookies policy, UAE hotels, Dubai hotel booking"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="UAE Dubai Hotels" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href="https://uaedubaihotels.com/privacy-policy"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="UAE Dubai Hotels" />
                <meta property="og:locale" content="en_US" />

                <meta
                    property="og:title"
                    content="Privacy Policy | UAE Dubai Hotels"
                />

                <meta
                    property="og:description"
                    content="Learn how UAE Dubai Hotels collects, stores, uses, and protects your personal information while providing hotel booking services across the UAE."
                />

                <meta
                    property="og:url"
                    content="https://uaedubaihotels.com/privacy-policy"
                />

                <meta
                    property="og:image"
                    content="https://uaedubaihotels.com/images/default-og.png"
                />

                {/* Twitter */}
                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Privacy Policy | UAE Dubai Hotels"
                />

                <meta
                    name="twitter:description"
                    content="Read the Privacy Policy of UAE Dubai Hotels to understand how we protect your personal information and privacy."
                />

                <meta
                    name="twitter:image"
                    content="https://uaedubaihotels.com/images/default-og.png"
                />
            </Helmet>

            <div className="
    w-full
    bg-[#FCFAF5]
    dark:bg-gray-950
">

                <PageHeader
                    title="Privacy Policy"
                    subtitle="Your privacy matters. Learn how UAEDiscountedHotels collects, uses, stores, and protects your information."
                />

                <div className="
    max-w-6xl
    mx-auto
    px-6
    py-20
    grid
    md:grid-cols-4
    gap-10
">

                    {/* SIDEBAR */}
                    <div className="md:col-span-1">

                        <div
                            className="
sticky
top-24

bg-white
dark:bg-gray-900

border
border-[#DDD2BA]
dark:border-gray-800

rounded-[35px]

p-6

shadow-xl
"
                        >
                            <h3 className="
font-semibold
text-[#0F766E]
dark:text-[#B89442]
mb-4
">

                                Quick Links
                            </h3>

                            <div className="space-y-3">
                                {[
                                    "Introduction",
                                    "Information We Collect",
                                    "How We Use Data",
                                    "Security",
                                    "Sharing",
                                    "Cookies",
                                    "Your Rights",
                                    "Contact",
                                ].map((item, i) => (
                                    <a
                                        key={i}
                                        href={`#section-${i}`}
                                        className="
                      block text-sm
                      text-gray-600 dark:text-gray-400
                      hover:text-[#B89442]
dark:hover:text-[#B89442]
                      transition
                    "
                                    >
                                        {item}
                                    </a>
                                ))}
                            </div>
                        </div>

                    </div>

                    {/* CONTENT */}
                    <div className="md:col-span-3">

                        <div
                            className="
                bg-white
dark:bg-gray-900

border
border-[#DDD2BA]
dark:border-gray-800

rounded-[40px]

p-8
md:p-10
              "
                        >

                            <Section
                                id="section-0"
                                icon={<FaInfoCircle />}
                                title="Introduction"
                                text="UAEDiscountedHotels respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and safeguard your data when you use our travel services."
                            />

                            <Section
                                id="section-1"
                                icon={<FaUserSecret />}
                                title="Information We Collect"
                                list={[
                                    "Personal information such as name, email address, and phone number",
                                    "Booking details and travel preferences",
                                    "Payment information processed through secure payment providers",
                                    "Technical information including browser, device, and usage analytics",
                                ]}
                            />

                            <Section
                                id="section-2"
                                icon={<FaInfoCircle />}
                                title="How We Use Your Information"
                                list={[
                                    "Process hotel, flight, tour, and travel bookings",
                                    "Provide booking confirmations and customer support",
                                    "Improve website functionality and user experience",
                                    "Send important service updates and travel notifications",
                                    "Meet legal and regulatory requirements",
                                ]}
                            />

                            <Section
                                id="section-3"
                                icon={<FaLock />}
                                title="Data Security"
                                text="We implement industry-standard security measures, encryption technologies, and secure systems to protect your personal information against unauthorized access, disclosure, or misuse."
                            />

                            <Section
                                id="section-4"
                                icon={<FaShieldAlt />}
                                title="Sharing of Information"
                                text="We do not sell, rent, or trade your personal information. Data may only be shared with trusted travel partners, payment providers, and service suppliers when necessary to fulfill your booking or comply with legal obligations."
                            />

                            <Section
                                id="section-5"
                                icon={<FaCookieBite />}
                                title="Cookies & Tracking Technologies"
                                text="Our website uses cookies and similar technologies to improve functionality, analyze website traffic, remember preferences, and enhance your browsing experience. You may manage cookie preferences through your browser settings."
                            />

                            <Section
                                id="section-6"
                                icon={<FaUserSecret />}
                                title="Your Rights"
                                text="Depending on applicable laws, you may request access to your personal data, correction of inaccurate information, deletion of data, or withdrawal of consent for specific processing activities."
                            />

                            <Section
                                id="section-7"
                                icon={<FaEnvelope />}
                                title="Contact Us"
                                text="If you have any questions regarding this Privacy Policy or how your personal information is handled, please contact our customer support team."
                            />

                            {/* CTA */}
                            <div
                                className="
rounded-[35px]

bg-[#0F766E]

text-white

p-8

text-center

shadow-xl
"
                            >
                                <h3 className="text-2xl font-semibold mb-3">
                                    Questions About Your Privacy?
                                </h3>

                                <p className="
text-white/80
mb-5
">
                                    Our team is available to answer any questions regarding data protection and privacy.
                                </p>

                                <a
                                    href="mailto:info@uaediscountedhotels.com"
                                    className="
inline-flex
items-center
gap-2

bg-[#B89442]

text-white

px-6
py-3

rounded-full

font-semibold

hover:bg-white
hover:text-[#0F766E]

transition
"
                                >
                                    <FaEnvelope />
                                    Contact Support
                                </a>
                            </div>

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

function Section({ id, icon, title, text, list }) {
    return (
        <div
            id={id}
            className="
        scroll-mt-28
        mb-10
        last:mb-0
    "
        >
            <div className="flex items-center gap-3 mb-4">

                <div
                    className="
w-12
h-12

rounded-2xl

flex
items-center
justify-center

bg-[#0F766E]

text-white

shadow-md
"
                >
                    {icon}
                </div>

                <h2 className="
text-xl
font-semibold
text-gray-900
dark:text-white
">
                    {title}
                </h2>
            </div>

            {text && (
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {text}
                </p>
            )}

            {list && (
                <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-2">
                    {list.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}
